import { useState, useEffect } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BookOpen, ChevronDown, ChevronUp, Loader2, Save, Plus, Trash2, GripVertical } from "lucide-react";

const API = `${getApiUrl()}/api`;

/**
 * GuidelinesDisplay - Read-only display of community guidelines (for users)
 */
export function GuidelinesDialog({ open, onOpenChange }) {
  const [guidelines, setGuidelines] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      setLoading(true);
      axios.get(`${API}/community/guidelines`)
        .then(res => setGuidelines(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-400">
            <BookOpen className="w-5 h-5" />
            Community Submission Guidelines
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Please review these guidelines before sharing content with the community.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {(guidelines?.sections || []).map((section, i) => (
              <div key={section.id || i} className="bg-white/5 rounded-lg border border-white/10 p-4">
                <h3 className="text-white font-medium text-sm mb-2">{section.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed whitespace-pre-wrap">{section.content}</p>
              </div>
            ))}
            {guidelines?.lastUpdatedAt && (
              <p className="text-xs text-white/20 text-right">
                Last updated: {new Date(guidelines.lastUpdatedAt).toLocaleDateString()}
                {guidelines.lastUpdatedBy && ` by ${guidelines.lastUpdatedBy}`}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * GuidelinesInlineSummary - Collapsible summary for inside the Share dialog
 */
export function GuidelinesInlineSummary() {
  const [expanded, setExpanded] = useState(false);
  const [guidelines, setGuidelines] = useState(null);

  useEffect(() => {
    axios.get(`${API}/community/guidelines`)
      .then(res => setGuidelines(res.data))
      .catch(() => {});
  }, []);

  if (!guidelines?.sections?.length) return null;

  return (
    <div className="border border-emerald-500/20 rounded-lg bg-emerald-500/5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 text-left"
        data-testid="guidelines-toggle"
      >
        <span className="flex items-center gap-2 text-emerald-400 text-xs font-medium">
          <BookOpen className="w-3.5 h-3.5" />
          Community Submission Guidelines
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-emerald-400" /> : <ChevronDown className="w-4 h-4 text-emerald-400" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {guidelines.sections.map((section, i) => (
            <div key={section.id || i}>
              <p className="text-white/70 text-xs font-medium">{section.title}</p>
              <p className="text-white/40 text-xs leading-relaxed">{section.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * AdminGuidelinesDialog - Admin dialog to edit community guidelines
 */
export function AdminGuidelinesDialog({ open, onOpenChange }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      axios.get(`${API}/community/guidelines`)
        .then(res => setSections(res.data?.sections || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/community/admin/guidelines`, { sections });
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to save guidelines:", err);
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (index, field, value) => {
    setSections(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const addSection = () => {
    setSections(prev => [...prev, { id: `section-${Date.now()}`, title: "", content: "" }]);
  };

  const removeSection = (index) => {
    setSections(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-400">
            <BookOpen className="w-5 h-5" />
            Edit Community Guidelines
          </DialogTitle>
          <DialogDescription className="text-white/60">
            These guidelines are shown to users when they share content with the community.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {sections.map((section, i) => (
              <div key={section.id || i} className="bg-white/5 rounded-lg border border-white/10 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-white/20 flex-shrink-0" />
                  <Input
                    value={section.title}
                    onChange={(e) => updateSection(i, "title", e.target.value)}
                    placeholder="Section title..."
                    className="bg-black/50 border-white/10 text-sm font-medium"
                    data-testid={`guideline-title-${i}`}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeSection(i)}
                    className="text-red-400/60 hover:text-red-400 flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <Textarea
                  value={section.content}
                  onChange={(e) => updateSection(i, "content", e.target.value)}
                  placeholder="Guideline content..."
                  className="bg-black/50 border-white/10 text-sm min-h-[80px] resize-none"
                  data-testid={`guideline-content-${i}`}
                />
              </div>
            ))}

            <Button
              variant="outline"
              onClick={addSection}
              className="w-full border-dashed border-white/20 text-white/50"
              data-testid="add-guideline-section"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Section
            </Button>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1 border-white/20"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-guidelines-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Guidelines
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
